import React, { useState } from 'react';
import { Location, Service, CustomerInfo } from '../../types';
import { BloomBottomCTA, BubbleInput, Hint, SummaryRow } from '../UI/Bloom/flow';
import { T, rgba } from '../../utils/adminTheme';

interface CustomerFormProps {
  customerInfo: CustomerInfo;
  onCustomerInfoChange: (info: Partial<CustomerInfo>) => void;
  selectedService: Service;
  selectedLocation: Location;
  selectedDate: Date;
  selectedTime: string;
  onBack: () => void;
  onComplete: () => void;
  onShowDatenschutz?: () => void;
  effectiveDuration?: number;
  customerProfile?: { name?: string; service_durations?: Record<string, number> } | null;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+\d][\d\s\-()]{6,}$/;

export const CustomerForm: React.FC<CustomerFormProps> = ({
  customerInfo,
  onCustomerInfoChange,
  selectedService,
  selectedLocation,
  selectedDate,
  selectedTime,
  onBack,
  onComplete,
  onShowDatenschutz,
  effectiveDuration,
  customerProfile,
}) => {
  const [dsgvo, setDsgvo] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const emailValid = EMAIL_RE.test(customerInfo.email);
  const phoneValid = PHONE_RE.test(customerInfo.phone);
  const isComplete = Boolean(
    customerInfo.name.trim() &&
    emailValid &&
    phoneValid &&
    dsgvo
  );

  const blur = (field: string) => setTouched(p => ({ ...p, [field]: true }));
  const price = `CHF ${selectedService.price.toFixed(2)}`;

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <style>{`
        @media (max-width: 600px) {
          .cf-aside { order: -1; }
          .cf-aside h2 { font-size: 20px !important; margin-bottom: 0 !important; }
        }
      `}</style>
      <div className="cf-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 16,
        alignItems: 'start',
      }}>
        <section style={{
          background: T.surface,
          border: `1px solid ${T.line}`,
          borderRadius: T.r.lg,
          padding: 18,
          display: 'grid',
          gap: 12,
        }}>
          <BubbleInput
            label="Vollständiger Name"
            value={customerInfo.name}
            onChange={name => onCustomerInfoChange({ name })}
            placeholder="z.B. Maria Müller"
            onBlur={() => blur('name')}
            error={touched.name && !customerInfo.name.trim() ? 'Name erforderlich' : undefined}
          />
          <BubbleInput
            label="E-Mail-Adresse"
            value={customerInfo.email}
            onChange={email => onCustomerInfoChange({ email })}
            placeholder="ihre@email.ch"
            type="email"
            onBlur={() => blur('email')}
            error={touched.email && !emailValid ? 'Ungültige E-Mail-Adresse' : undefined}
          />
          <BubbleInput
            label="Telefonnummer"
            value={customerInfo.phone}
            onChange={phone => onCustomerInfoChange({ phone })}
            placeholder="+41 79 123 45 67"
            type="tel"
            onBlur={() => blur('phone')}
            error={touched.phone && !phoneValid ? 'Ungültige Telefonnummer' : undefined}
          />

          <label style={{ display: 'block' }}>
            <div style={{
              fontFamily: T.body,
              fontSize: 11,
              color: T.muted,
              marginBottom: 6,
              letterSpacing: '0.06em',
            }}>
              Anmerkungen optional
            </div>
            <textarea
              value={customerInfo.notes || ''}
              onChange={e => onCustomerInfoChange({ notes: e.target.value })}
              rows={4}
              placeholder="Besondere Wünsche oder Hinweise"
              style={{
                width: '100%',
                resize: 'vertical',
                border: `1px solid ${T.line}`,
                borderRadius: T.r.lg,
                background: T.surface,
                padding: '13px 16px',
                color: T.text,
                fontFamily: T.body,
                fontSize: 15,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </label>

          {/* DSGVO-Checkbox */}
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
            <div
              onClick={() => setDsgvo(v => !v)}
              style={{
                width: 20, height: 20, flexShrink: 0, marginTop: 2,
                borderRadius: 5,
                border: `1.5px solid ${dsgvo ? T.accent : T.line}`,
                background: dsgvo ? T.accent : T.surface,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all .15s',
              }}
            >
              {dsgvo && <svg width="11" height="9" viewBox="0 0 11 9" fill="none"><path d="M1 4.5L4 7.5L10 1" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
            </div>
            <span style={{ fontFamily: T.body, fontSize: 12, color: T.muted, lineHeight: 1.5 }}>
              Ich habe die{' '}
              <button
                type="button"
                onClick={onShowDatenschutz}
                style={{ color: T.accent, textDecoration: 'underline', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: T.body, fontSize: 12 }}
              >
                Datenschutzerklärung
              </button>{' '}
              gelesen und bin mit der Verarbeitung meiner Daten zur Terminbuchung einverstanden.*
            </span>
          </label>

          <Hint>
            * Pflichtfeld. Ihre Daten werden nur zur Terminverwaltung verwendet.
          </Hint>
        </section>

        <aside className="cf-aside" style={{
          background: T.surface,
          border: `1px solid ${T.line}`,
          borderRadius: T.r.lg,
          padding: 0,
          display: 'grid',
          gap: 0,
          overflow: 'hidden',
        }}>
          {/* Service Bild */}
          {selectedService.image_url && selectedService.image_visible && (
            <img
              src={selectedService.image_url}
              alt={selectedService.name}
              style={{
                width: '100%',
                height: 200,
                objectFit: 'cover',
                display: 'block',
              }}
            />
          )}
          <div style={{ padding: 18, display: 'grid', gap: 12 }}>
            <h2 style={{
              margin: '0 0 4px',
              fontFamily: T.display,
              fontStyle: 'italic',
              fontSize: 27,
              fontWeight: 500,
            }}>
              Ihre Buchung
            </h2>
          <SummaryRow k="Behandlung" v={selectedService.name} />
          <SummaryRow
            k="Dauer"
            v={
              effectiveDuration && effectiveDuration !== selectedService.duration
                ? <span>
                    <span style={{ textDecoration: 'line-through', color: T.muted, marginRight: 6 }}>{selectedService.duration} Min.</span>
                    <span style={{ color: T.accent, fontWeight: 600 }}>{effectiveDuration} Min.</span>
                    <span style={{ fontSize: 10, background: `rgba(${T.accent},0.1)`, color: T.accent, borderRadius: 4, padding: '1px 6px', marginLeft: 6 }}>Pers.</span>
                  </span>
                : `${selectedService.duration} Minuten`
            }
          />
          <SummaryRow k="Standort" v={selectedLocation.name} />
          <SummaryRow
            k="Datum"
            v={selectedDate.toLocaleDateString('de-DE', {
              weekday: 'short',
              day: 'numeric',
              month: 'long',
            })}
          />
          <SummaryRow k="Uhrzeit" v={`${selectedTime} Uhr`} />
          <div style={{ height: 1, background: T.lineSoft, margin: '4px 0' }} />
          <SummaryRow k="Gesamtpreis" v={price} bold />
          {selectedService.prepayment && (
            <SummaryRow
              k="Anzahlung"
              v={`CHF ${selectedService.prepayment_amount?.toFixed(2)}`}
            />
          )}
          </div>
        </aside>
      </div>

      <BloomBottomCTA
        label={selectedService.prepayment ? 'Zur Zahlung' : 'Termin buchen'}
        onClick={onComplete}
        disabled={!isComplete}
        summary={{ label: 'Gesamtpreis', value: price }}
        secondary={{ label: 'Zurück zur Uhrzeit', onClick: onBack }}
      />
    </div>
  );
};
